import {
  Autocomplete,
  Box,
  Grid,
  InputAdornment,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { SearchOutlined } from '@mui/icons-material';
import { ClientControllerAction, ClientControllerState } from '../../controller/client-controller';
import { PositionModel } from '../../../position/model/position.model';
import { MinistryModel, ProvinceModel } from '../../../documents/models/destination.model';
import { DepartmentModel } from '../../../department/model/department.model';
import { useEffect, useState } from 'react';
import { useDebounce } from '../../../../hooks/use-debounce';

interface IProps {
  ctrl: ClientControllerAction & ClientControllerState;
}

const Header = ({ ctrl }: IProps) => {
  const [search, setSearch] = useState('');
  const debouncedText = useDebounce(search ?? '', 500);
  useEffect(() => {
    const body = { ...ctrl.filter };
    delete body.phone;
    delete body.email;
    if (debouncedText === '') {
      ctrl.onFilterChange(body);
    } else if (debouncedText.includes('@')) {
      ctrl.onFilterChange({ ...body, email: debouncedText });
    } else {
      ctrl.onFilterChange({ ...body, phone: debouncedText });
    }
  }, [debouncedText]);

  return (
    <Box sx={{ py: 3, px: 3 }}>
      <Grid container spacing={1}>
        <Grid item xs={12} md={2.4}>
          <TextField
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            size="small"
            placeholder="ເບີໂທ ຫຼື ອີເມວ"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlined />
                </InputAdornment>
              ),
              style: {
                borderRadius: '30px',
              },
            }}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} md={2.4}>
          <Autocomplete
            key={ctrl?.filter?.positionData?.uuid}
            noOptionsText={'ບໍ່ພົບຂໍ້ມູນ'}
            disablePortal
            id="combo-box-demo"
            value={ctrl?.filter?.positionData}
            size={'small'}
            onChange={(_, event) =>
              ctrl.onFilterChange({
                ...ctrl?.filter,
                position_id: event?.uuid,
                positionData: event ?? undefined,
              })
            }
            options={ctrl?.listPosition}
            getOptionLabel={(op: PositionModel) => op?.position_name_la ?? ''}
            isOptionEqualToValue={(option, value) => option.uuid === value?.uuid}
            renderOption={(_props, option: PositionModel) => {
              return (
                <Box key={option.uuid}>
                  <MenuItem
                    onClick={() => {
                      ctrl.onFilterChange({
                        ...ctrl?.filter,
                        position_id: option.uuid,
                        positionData: option,
                      });
                    }}
                    value={option.uuid}
                  >
                    <Typography sx={{ fontSize: '15px' }} marginLeft={2}>
                      {option?.position_name_la}
                    </Typography>
                  </MenuItem>
                </Box>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  ...params.InputProps,
                  style: {
                    borderRadius: '25px',
                  },
                }}
                inputProps={{
                  ...params.inputProps,
                  style: {
                    borderRadius: '25px',
                  },
                }}
                placeholder={'ຄົ້ນຫາຕຳແໜ່ງ'}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} md={2.4}>
          <Autocomplete
            key={ctrl?.filter?.ministryData?.uuid}
            noOptionsText={'ບໍ່ພົບຂໍ້ມູນ'}
            disablePortal
            id="combo-box-demo-2"
            value={ctrl?.filter?.ministryData}
            size={'small'}
            onChange={(_, event) =>
              ctrl.onFilterChange({
                ...ctrl?.filter,
                ministry_id: event?.uuid,
                ministryData: event ?? undefined,
              })
            }
            options={ctrl?.listMinistry}
            getOptionLabel={(op: MinistryModel) => op?.title_la ?? ''}
            isOptionEqualToValue={(option, value) => option.uuid === value?.uuid}
            renderOption={(_props, option: MinistryModel) => {
              return (
                <Box key={option.uuid}>
                  <MenuItem
                    onClick={() => {
                      ctrl.onFilterChange({
                        ...ctrl?.filter,
                        ministry_id: option.uuid,
                        ministryData: option,
                      });
                    }}
                    value={option.uuid}
                  >
                    <Typography sx={{ fontSize: '15px' }} marginLeft={2}>
                      {option?.title_la}
                    </Typography>
                  </MenuItem>
                </Box>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  ...params.InputProps,
                  style: {
                    borderRadius: '25px',
                  },
                }}
                inputProps={{
                  ...params.inputProps,
                  style: {
                    borderRadius: '25px',
                  },
                }}
                placeholder={'ຄົ້ນຫາກະຊວງ ຫລື ອົງການທຽບເທົ່າ'}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} md={2.4}>
          <Autocomplete
            key={ctrl?.filter?.provinceData?.uuid}
            noOptionsText={'ບໍ່ພົບຂໍ້ມູນ'}
            disablePortal
            id="combo-box-demo"
            value={ctrl?.filter?.provinceData}
            size={'small'}
            onChange={(_, event) =>
              ctrl.onFilterChange({
                ...ctrl?.filter,
                province_id: event?.uuid,
                provinceData: event ?? undefined,
              })
            }
            options={ctrl?.listProvince}
            getOptionLabel={(op: ProvinceModel) => op?.title_la ?? ''}
            isOptionEqualToValue={(option, value) => option.uuid === value?.uuid}
            renderOption={(_props, option: ProvinceModel) => {
              return (
                <Box key={option.uuid}>
                  <MenuItem
                    onClick={() => {
                      ctrl.onFilterChange({
                        ...ctrl?.filter,
                        province_id: option.uuid,
                        provinceData: option,
                      });
                    }}
                    value={option.uuid}
                  >
                    <Typography sx={{ fontSize: '15px' }} marginLeft={2}>
                      {option?.title_la}
                    </Typography>
                  </MenuItem>
                </Box>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  ...params.InputProps,
                  style: {
                    borderRadius: '25px',
                  },
                }}
                inputProps={{
                  ...params.inputProps,
                  style: {
                    borderRadius: '25px',
                  },
                }}
                placeholder={'ຄົ້ນຫາແຂວງ'}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} md={2.4}>
          <Autocomplete
            key={ctrl?.filter?.departmentData?.uuid}
            noOptionsText={'ບໍ່ພົບຂໍ້ມູນ'}
            disablePortal
            id="combo-box-department"
            value={ctrl?.filter?.departmentData}
            size={'small'}
            onChange={(_, event) =>
              ctrl.onFilterChange({
                ...ctrl?.filter,
                department_id: event?.uuid,
                departmentData: event ?? undefined,
              })
            }
            options={ctrl?.listDepartment}
            getOptionLabel={(op: DepartmentModel) => op?.title_la ?? ''}
            isOptionEqualToValue={(option, value) => option.uuid === value?.uuid}
            renderOption={(_props, option: DepartmentModel) => {
              return (
                <Box key={option.uuid}>
                  <MenuItem
                    onClick={() => {
                      ctrl.onFilterChange({
                        ...ctrl?.filter,
                        department_id: option.uuid,
                        departmentData: option,
                      });
                    }}
                    value={option.uuid}
                  >
                    <Typography sx={{ fontSize: '15px' }} marginLeft={2}>
                      {option?.title_la}
                    </Typography>
                  </MenuItem>
                </Box>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  ...params.InputProps,
                  style: { borderRadius: '25px' },
                }}
                inputProps={{
                  ...params.inputProps,
                  style: { borderRadius: '25px' },
                }}
                placeholder={'ຄົ້ນຫາກົມ'}
              />
            )}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default Header;
